import React from 'react';

export type RequiredContext<ContextValue> = React.Context<ContextValue> & {
  useContext: () => ContextValue;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RequiredContextValues<Context extends RequiredContext<any>> =
  Context extends RequiredContext<infer Values> ? Values : never;

export function createRequiredContext<ContextValue>(
  displayName: string,
): RequiredContext<ContextValue> {
  if (process.env.NODE_ENV === 'development') {
    // make our context more distinguishable in React devtools
    // eslint-disable-next-line no-param-reassign
    displayName = `$HA_${displayName}`;
  }

  const Context = React.createContext<ContextValue | undefined>(undefined);

  Context.displayName = displayName;

  const requiredContext = Context as unknown as RequiredContext<ContextValue>;

  // Keep a reference to the original Consumer to avoid recursion after override
  const OriginalConsumer = Context.Consumer;

  // @ts-expect-error TS complains about missing `$$typeof`, however that is no issue.
  const RequiredConsumer: React.Consumer<ContextValue> = ({
    children,
  }: React.ConsumerProps<ContextValue>) => (
    <OriginalConsumer>
      {value => {
        if (value === undefined) {
          throw new Error(
            `Context "${displayName}" was required by <${displayName}.Consumer /> but not provided in the parent tree`,
          );
        }

        return children(value);
      }}
    </OriginalConsumer>
  );

  // @ts-ignore
  RequiredConsumer.displayName = `${displayName}.RequiredConsumer`;

  requiredContext.Consumer = RequiredConsumer;

  const useContext = () => {
    const value = React.useContext(Context);

    if (value === undefined) {
      throw new Error(
        `Context "${displayName}" was required by ${displayName}.useContext() but not provided in the parent tree`,
      );
    }

    return value!;
  };

  requiredContext.useContext = useContext;

  return requiredContext;
}
